import { useEffect, useState } from "react";
import clsx from "clsx";
import type { ActivityEvent } from "@edge/shared";
import { EmailDetailModal } from "./EmailDetailModal";
import { ActivityFullFeedModal } from "./ActivityFullFeedModal";

type FilterMode = "deals" | "all";
const FILTER_LS_KEY = "edge_activity_filter";

function loadFilter(): FilterMode {
  if (typeof window === "undefined") return "deals";
  try {
    const v = window.localStorage.getItem(FILTER_LS_KEY);
    return v === "all" ? "all" : "deals";
  } catch {
    return "deals";
  }
}
function saveFilter(v: FilterMode) {
  try {
    window.localStorage.setItem(FILTER_LS_KEY, v);
  } catch {
    /* localStorage disabled — feature degrades to session-scoped, fine. */
  }
}

interface ActivityFeedProps {
  events: ActivityEvent[];
  /** Count of events suppressed by noise filter — banner shows when > 0. */
  noiseSuppressed?: number;
  /** CK-1: hosted inside WatchtowerActivityPanel — render body only (no panel
   *  shell/header), locked to the all-events view (no DEALS/ALL pills). */
  embedded?: boolean;
}

/**
 * Right-rail activity feed. Compressed two-line rows, click to expand inline
 * for a quick preview. Email events also offer OPEN FULL EMAIL → modal.
 * EXPAND FULL FEED at the bottom opens the unfiltered list with noise visible.
 */
export function ActivityFeed({
  events,
  noiseSuppressed = 0,
  embedded = false,
}: ActivityFeedProps) {
  const [filter, setFilter] = useState<FilterMode>(loadFilter);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [emailModalId, setEmailModalId] = useState<string | null>(null);
  const [fullFeedOpen, setFullFeedOpen] = useState(false);

  // Persist filter selection. Trivially small write.
  useEffect(() => {
    saveFilter(filter);
  }, [filter]);

  // Embedded view is locked to all-events; standalone honors the DEALS/ALL pill.
  const visible =
    embedded || filter === "all" ? events : events.filter((e) => e.dealId);

  // Shared body content — identical in the embedded and standalone shells.
  const inner = (
    <>
      {noiseSuppressed > 0 && (
        <button
          className="activity-noise-banner"
          onClick={() => setFullFeedOpen(true)}
          title="View full feed including suppressed noise"
        >
          ⚠ FILTERING {noiseSuppressed} NOISE · IG, BLASTS, SPAM
        </button>
      )}

      <div className="activity-panel-body">
        {visible.length === 0 && (
          <div className="activity-empty">
            {!embedded && filter === "deals"
              ? "No deal-tagged events. Try ALL."
              : "No recent activity."}
          </div>
        )}
        {visible.map((evt) => (
          <ActivityRow
            key={evt.id}
            event={evt}
            expanded={expandedId === evt.id}
            onToggle={() =>
              setExpandedId((cur) => (cur === evt.id ? null : evt.id))
            }
            onOpenEmail={(id) => setEmailModalId(id)}
          />
        ))}
      </div>

      <button
        className="activity-expand-btn"
        onClick={() => setFullFeedOpen(true)}
      >
        ▾ EXPAND FULL FEED
      </button>
    </>
  );

  const modals = (
    <>
      {fullFeedOpen && (
        <ActivityFullFeedModal
          onClose={() => setFullFeedOpen(false)}
          onOpenEmail={(id) => setEmailModalId(id)}
        />
      )}
      {emailModalId && (
        <EmailDetailModal
          messageId={emailModalId}
          onClose={() => setEmailModalId(null)}
        />
      )}
    </>
  );

  // Embedded (CK-1): body only — the WatchtowerActivityPanel tab owns the shell
  // and title, and the DEALS/ALL pills are dropped (locked to all events).
  if (embedded) {
    return (
      <>
        <div className="activity-embedded">{inner}</div>
        {modals}
      </>
    );
  }

  return (
    <div className="activity-rail">
      <div className="activity-panel">
        <span className="bracket tl" />
        <span className="bracket tr" />
        <span className="bracket bl" />
        <span className="bracket br" />

        <div className="activity-panel-header">
          <div className="activity-panel-title">LIVE ACTIVITY</div>
          <div className="activity-filter-group">
            <button
              className={clsx(
                "activity-filter-pill",
                filter === "deals" && "active",
              )}
              onClick={() => setFilter("deals")}
              title="Show only events tied to an open deal"
            >
              DEALS
            </button>
            <button
              className={clsx(
                "activity-filter-pill",
                filter === "all" && "active",
              )}
              onClick={() => setFilter("all")}
              title="Show all live events (still excludes noise)"
            >
              ALL
            </button>
          </div>
        </div>

        {inner}
      </div>

      {modals}
    </div>
  );
}

/**
 * One compressed row. Click anywhere on the title-row to toggle expand;
 * the OPEN FULL EMAIL button inside the preview stops propagation and fires
 * the modal trigger. Body preview text in the expanded view is selectable
 * (no onClick on the preview wrapper).
 */
function ActivityRow({
  event,
  expanded,
  onToggle,
  onOpenEmail,
  onRescue,
  rescued,
}: {
  event: ActivityEvent;
  expanded: boolean;
  onToggle: () => void;
  onOpenEmail: (id: string) => void;
  /** Noise-review only: one-click "Not noise" rescue. Absent on the rail. */
  onRescue?: (email: string) => void;
  /** True once this sender has been rescued in this session (shows confirmation). */
  rescued?: boolean;
}) {
  const isEmail = event.kind === "email" && event.email;
  const isAutoResolved =
    event.kind === "watchtower_auto_resolved" && event.watchtowerResolution;
  const out = isEmail && event.email!.direction === "out";
  // Outbound: the sender is you — show the recipient instead.
  const who = isEmail
    ? out
      ? `→ ${event.email!.to || "(recipient)"}`
      : event.email!.fromName
    : "";
  const headline = isEmail
    ? `${who}: ${event.email!.subject}`
    : isAutoResolved
      ? `◆ Watchtower auto-resolved: "${event.watchtowerResolution!.alertTitle}"`
      : event.kind;
  return (
    <div
      className={clsx(
        "activity-event",
        expanded && "expanded",
        event.noise && "is-noise",
        isAutoResolved && "is-auto-resolved",
      )}
    >
      <div className="activity-event-row" onClick={onToggle}>
        {/* Header line: time + chrome on the left, controls pushed right. */}
        <div className="activity-event-head">
          <div className="activity-time">{event.time}</div>
          <div className={clsx("activity-icon", event.icon)} />
          {isEmail && (
            <span
              className={clsx("activity-dir", out ? "out" : "in")}
              title={out ? "Outbound (you sent)" : "Inbound"}
            >
              {out ? "↗ OUT" : "↙ IN"}
            </span>
          )}
          {event.dealName && (
            <span className="activity-deal-pill" title={event.dealName}>
              {event.dealName.split(/[\s\-–—/]+/)[0].toUpperCase()}
            </span>
          )}
          {event.noise && !rescued && <span className="activity-noise-pill">NOISE</span>}
          {rescued && <span className="activity-rescued-pill">✓ KEPT</span>}
          <span className="activity-event-head-spacer" />
          {onRescue && event.noise && isEmail && !rescued && (
            <button
              className="activity-rescue-btn"
              title="Not noise — always show this sender"
              onClick={(e) => {
                e.stopPropagation();
                onRescue(event.email!.fromEmail);
              }}
            >
              ✓ Not noise
            </button>
          )}
          <span className="activity-expand-icon">▸</span>
        </div>
        {/* The activity itself — its own full-width line so it's readable. */}
        <div className="activity-text">{headline}</div>
      </div>

      {/* Expanded preview — text is selectable; OPEN FULL EMAIL stops propagation. */}
      <div className="activity-event-preview">
        {isEmail && (
          <>
            <div className="activity-preview-meta">
              <span className="activity-preview-from">
                {out
                  ? `To: ${event.email!.to || "(recipient)"}`
                  : `${event.email!.fromName} <${event.email!.fromEmail}>`}
              </span>
              <span className="activity-preview-time">{event.time}</span>
            </div>
            <div className="activity-preview-subject">
              {event.email!.subject}
            </div>
            <div className="activity-preview-body">
              {event.email!.bodyPreview || "(no preview available)"}
            </div>
            <button
              className="activity-preview-open"
              onClick={(e) => {
                e.stopPropagation();
                onOpenEmail(event.id);
              }}
            >
              OPEN FULL EMAIL ↗
            </button>
          </>
        )}
        {isAutoResolved && (
          <>
            <div className="activity-preview-meta">
              <span className="activity-preview-from">
                Watchtower · {event.watchtowerResolution!.alertSection.replace(/_/g, " ")}
              </span>
              <span className="activity-preview-time">{event.time}</span>
            </div>
            <div className="activity-preview-subject">
              {event.watchtowerResolution!.alertTitle}
            </div>
            <div className="activity-preview-body">
              <strong>How it cleared:</strong> {event.watchtowerResolution!.resolutionReason}
            </div>
            {event.watchtowerResolution!.aiReasoning && (
              <div className="activity-preview-body activity-preview-ai">
                <strong>AI reasoning:</strong> {event.watchtowerResolution!.aiReasoning}
              </div>
            )}
          </>
        )}
        {!isEmail && !isAutoResolved && (
          <div className="activity-preview-body">
            (No preview available for event kind: {event.kind})
          </div>
        )}
      </div>
    </div>
  );
}

/** Re-export for the full-feed modal which renders the same row. */
export { ActivityRow };
